// NOTE: These maps are extremely dirty and should be cleaned up once the admin dashboard is setup!

import {
  Type,
  Category,
  SubCategory,
  Identifier,
  Color,
  Pattern,
  Material,
  SizeType,
  SizeCategory,
  SizeSubCategory,
  SizeRegion,
  AlphaSize,
  ShoeWidth,
  Condition,
  Decade,
  SortBy,
  Technique,
  SubTechnique,
} from '@buf/upcyclers_gateway.bufbuild_es/product/v2/product_pb';

import {
  generateProtoEnumMappings,
  generateLowercaseProtoEnumMappings,
  capitalizedPerWord,
} from '@lib/utils';

enum Price {
  'Under $50' = 'maxPrice=50',
  'Under $75' = 'maxPrice=75',
  'Under $100' = 'maxPrice=100',
  'Over $100' = 'minPrice=100',
}

// Create new Type object spreading restType properties and adding SECONDHAND
// This effectively renames SECOND_HAND to SECONDHAND while keeping all other values
const { SECOND_HAND, ...restType } = Type;
void SECOND_HAND; // Mark as intentionally unused
const SECONDHAND: Type = Type.SECOND_HAND;
const Types = {
  ...restType,
  SECONDHAND,
};
const Materials = Material;
const PrimaryColors = Color;

const Identifiers = {
  BUTTON_UP: Identifier.BUTTON_UP,
  T_SHIRTS: Identifier.T_SHIRT,
  SWEATERS: Identifier.SWEATER,
  SWEATSHIRTS: Identifier.SWEATSHIRT,
  HOODIES: Identifier.HOODIE,
  TUNICS: Identifier.TUNIC,
  TANK_TOPS: Identifier.TANK_TOP,
  BUSTIERS: Identifier.BUSTIER,
  SKIRTS: Identifier.SKIRT,
  PANTS: Identifier.PANTS,
  JEANS: Identifier.JEANS,
  SHORTS: Identifier.SHORTS,
  MINI: Identifier.MINI,
  MIDI: Identifier.MIDI,
  MAXI: Identifier.MAXI,
  COATS: Identifier.COAT,
  JACKETS: Identifier.JACKET,
  VESTS: Identifier.VEST,
  PANT_SUITS: Identifier.PANT_SUITS,
  SKIRT_SUITS: Identifier.SKIRT_SUITS,
  BLAZERS: Identifier.BLAZERS,
};

export const ProductEnums = {
  Types,
  Category,
  SubCategory,
  Identifiers,
  PrimaryColors,
  Color,
  Pattern,
  Materials,
  SizeType,
  SizeCategory,
  SizeSubCategory,
  SizeRegion,
  AlphaSize,
  ShoeWidth,
  Condition,
  Decade,
  Price,
  Technique,
  SubTechnique,
};

type EnumDictionary = {
  [K in keyof typeof ProductEnums]: string;
};

export const PRODUCT_PROPERTIES: EnumDictionary = Object.keys(
  ProductEnums,
).reduce(
  (acc, key) => ({
    ...acc,
    [key]: key,
  }),
  {} as EnumDictionary,
);

/**
 * Generates human-readable values for enum mappings related to product properties.
 *
 * This function maps various product-related enums to easily readable key-value pairs,
 * making it convenient to use them in UI components, filtering, and dropdowns.
 */
const enumMappings = generateProtoEnumMappings(ProductEnums, {
  notFormatted: [PRODUCT_PROPERTIES.AlphaSize],
});

/**
 * Maps a given numeric value to its corresponding string label in the specified enum.
 *
 * @param enumName - The name of the enum to look up.
 * @param value - The numeric value to be mapped to a string.
 * @returns The corresponding string label if found, otherwise 'N/A'.
 */
export const mapProductEnumToString = (
  enumName: string,
  value: number,
): string => enumMappings[enumName]?.[value] ?? 'N/A';

export interface ProductOption {
  value: number;
  label: string;
}

/**
 * Retrieves and maps the enum values to an array of ProductOption objects.
 * - The options are ordered based on the provided enum name.
 * - The `orderLast` array allows specifying certain labels to be moved to the end of the array.
 *   - This can be useful for cases where you want specific options (e.g., 'multi', 'other') to appear last in the order.
 *
 * @param enumName - The name of the enum to retrieve options from.
 * @param orderLast - An optional array of labels to be ordered last. If not provided, no items will be reordered.
 * @returns An array of ProductOption objects with ordered values.
 */
export const mapProductEnumOptions = (
  enumName: string,
  orderLast: string[] = [],
): ProductOption[] => {
  // Convert the enum mappings into an array of ProductOption objects
  const options = Object.entries(enumMappings[enumName]).map(
    ([key, value]) => ({
      value: parseInt(key), // Convert string key to an integer
      label: value,
    }),
  );

  // Separate options where 'multi' and 'other' should be ordered last
  const restOfOptions = options.filter(
    (option) => !orderLast.includes(option.label),
  );
  const lastOptions = options.filter((option) =>
    orderLast.includes(option.label),
  );

  // Return options with 'multi' and 'other' appended at the end
  return [...restOfOptions, ...lastOptions];
};

export interface ProductFilterOption {
  value: string;
  label: string;
}

/**
 * Retrieves an array of product filter options from a given enum, allowing
 * specific labels to be ordered last.
 *
 * @param enumName - The name of the enum to retrieve options from.
 * @param orderLast - An optional array of labels that should be placed at the end of the list.
 * @returns An array of ProductFilterOption objects with the specified ordering.
 */
export const mapProductFilterOptions = (
  enumName: string,
  orderLast: string[] = [],
): ProductFilterOption[] => {
  // Convert the enum mappings into an array of ProductFilterOption objects
  const options = Object.entries(enumMappings[enumName]).map(
    ([key, value]) => ({
      value: key,
      label: value,
    }),
  );

  // Normalize labels to lowercase for case-insensitive comparison
  const restOfOptions = options.filter(
    (option) => !orderLast.includes(option.label.toLowerCase()),
  );
  const lastOptions = options.filter((option) =>
    orderLast.includes(option.label.toLowerCase()),
  );

  // Return options with specified values appended at the end
  return [...restOfOptions, ...lastOptions];
};

const lowercaseEnumMappings = generateLowercaseProtoEnumMappings({
  Types,
  Category,
  SubCategory,
  Identifiers,
  PrimaryColors,
  Materials,
});

// `mapStringToEnum` takes a label (e.g., 'category', 'material') and a string value (e.g., 'Tops', 'Cotton'),
// then returns the corresponding enum value from `stringMappings`. If the label or value is invalid, it throws an error.
// The function ensures that the label is normalized to match the key in `stringMappings`.
export const mapStringToEnum = (
  label: string,
  value: string,
): string | number => {
  // Capitalize the first letter of the label to match the keys in `stringMappings`
  const normalizedLabel = capitalizedPerWord(label);

  // Check if normalized label matches the keys in `stringMappings`
  if (!(normalizedLabel in lowercaseEnumMappings)) {
    return value;
  } else {
    const mappings =
      lowercaseEnumMappings[
        normalizedLabel as keyof typeof lowercaseEnumMappings
      ];

    // Otherwise, get the value of the normalized label
    const enumValue = mappings[value.replaceAll('-', '_')];

    // If no value is provided, then throw an error message
    if (enumValue === undefined) {
      throw new Error(
        `Undefined enum value for ${value} in ${normalizedLabel}`,
      );
    }

    return enumValue;
  }
};

type SortMappings = {
  [key: string]: { [key: string]: string | number };
};

// `sortMappings` is an object that maps string labels (e.g., sortBy, sortOrder) to their corresponding values.
// Each key corresponds to a category, and the values map specific string labels (like 'newest', 'asc') to their enum counterparts.
const sortMappings: SortMappings = {
  sortBy: {
    newest: SortBy.CREATED_AT,
    oldest: SortBy.CREATED_AT,
    price: SortBy.PRICE,
  },
  sortOrder: {
    asc: `true`,
    desc: `false`,
  },
};

// `mapStringToSort` takes a label (e.g., 'sortBy', 'sortOrder') and a string value (e.g., 'newest', 'asc'),
// then returns the corresponding enum value from `sortMappings`. If the label or value is invalid, it throws an error.
// The function ensures that the label is normalized to match the key in `sortMappings`.
export const mapStringToSort = (
  label: string,
  value: string,
): string | number => {
  // Check if normalized label matches the keys in `sortMappings`
  if (!(label in sortMappings)) {
    return value;
  } else {
    const mappings = sortMappings[label as keyof typeof lowercaseEnumMappings];

    // Otherwise, get the value of the normalized label
    const sortValue = mappings[value];
    // If no value is provided, then throw an error message
    if (sortValue === undefined) {
      throw new Error(`Undefined sort value for ${value} in ${label}`);
    }

    return sortValue;
  }
};

type FilterOption = {
  label: string;
  param: string;
};

export type CategoryMap = {
  [C in Category]?: FilterOption;
};
export const CATEGORY_MAPPINGS: CategoryMap = {
  [Category.CLOTHING]: {
    label: 'Clothing',
    param: 'clothing',
  },
  [Category.ACCESSORIES]: {
    label: 'Accessories',
    param: 'accessories',
  },
  [Category.JEWELRY]: {
    label: 'Jewelry',
    param: 'jewelry',
  },
  [Category.FOOTWEAR]: {
    label: 'Footwear',
    param: 'footwear',
  },
  [Category.FABRIC]: {
    label: 'Fabric',
    param: 'fabric',
  },
  [Category.NOTIONS]: {
    label: 'Notions',
    param: 'notions',
  },
  [Category.ODDS_AND_ENDS]: {
    label: 'Odds & Ends',
    param: 'odds-and-ends',
  },
};

export type SubCategoryMap = {
  [C in Category]?: {
    [S in SubCategory]?: FilterOption;
  };
};
export const SUBCATEGORY_MAPPINGS: SubCategoryMap = {
  [Category.CLOTHING]: {
    [SubCategory.TOPS]: {
      label: 'Tops',
      param: 'tops',
    },
    [SubCategory.BOTTOMS]: {
      label: 'Bottoms',
      param: 'bottoms',
    },
    [SubCategory.DRESSES]: {
      label: 'Dresses',
      param: 'dresses',
    },
    [SubCategory.OUTERWEAR]: {
      label: 'Outerwear',
      param: 'outerwear',
    },
    [SubCategory.SUITS_AND_BLAZERS]: {
      label: 'Suits & Blazers',
      param: 'suits-and-blazers',
    },
    [SubCategory.JUMPSUIT]: {
      label: 'Jumpsuit',
      param: 'jumpsuit',
    },
  },
  [Category.ACCESSORIES]: {
    [SubCategory.BAGS]: {
      label: 'Bags',
      param: 'bags',
    },
    [SubCategory.HATS]: {
      label: 'Hats',
      param: 'hats',
    },
    [SubCategory.SCARVES]: {
      label: 'Scarves',
      param: 'scarves',
    },
  },
  [Category.JEWELRY]: {
    [SubCategory.EARRINGS]: {
      label: 'Earrings',
      param: 'earrings',
    },
    [SubCategory.NECKLACES]: {
      label: 'Necklaces',
      param: 'necklaces',
    },
    [SubCategory.BRACELETS]: {
      label: 'Bracelets',
      param: 'bracelets',
    },
    [SubCategory.RINGS]: {
      label: 'Rings',
      param: 'rings',
    },
  },
  [Category.FOOTWEAR]: {
    [SubCategory.SNEAKERS]: {
      label: 'Sneakers',
      param: 'sneakers',
    },
    [SubCategory.SANDALS]: {
      label: 'Sandals',
      param: 'sandals',
    },
    [SubCategory.BOOTS]: {
      label: 'Boots',
      param: 'boots',
    },
    [SubCategory.HEELS]: {
      label: 'Heels',
      param: 'heels',
    },
    [SubCategory.FLATS]: {
      label: 'Flats',
      param: 'flats',
    },
  },
  [Category.FABRIC]: {
    [SubCategory.REMNANTS]: {
      label: 'Remnants',
      param: 'remnants',
    },
    [SubCategory.SCRAPS]: {
      label: 'Scraps',
      param: 'scraps',
    },
    [SubCategory.BOLTS_AND_ROLLS]: {
      label: 'Bolts & Rolls',
      param: 'bolts-and-rolls',
    },
    [SubCategory.BEDDING]: {
      label: 'Bedding',
      param: 'bedding',
    },
    [SubCategory.CURTAINS]: {
      label: 'Curtains',
      param: 'curtains',
    },
  },
  [Category.NOTIONS]: {},
  [Category.ODDS_AND_ENDS]: {
    [SubCategory.PLUSH]: {
      label: 'Plush',
      param: 'plush',
    },
    [SubCategory.TRINKETS]: {
      label: 'Trinkets',
      param: 'trinkets',
    },
    [SubCategory.NOVELTIES]: {
      label: 'Novelties',
      param: 'novelties',
    },
  },
};

export type IdentifierMap = {
  [C in Category]?: {
    [S in SubCategory]?: {
      [I in Identifier]?: FilterOption;
    };
  };
};
export const IDENTIFIER_MAPPINGS: IdentifierMap = {
  [Category.CLOTHING]: {
    [SubCategory.TOPS]: {
      [Identifier.BUTTON_UP]: {
        label: 'Button Up',
        param: 'button-up',
      },
      [Identifier.T_SHIRT]: {
        label: 'T Shirts',
        param: 't-shirts',
      },
      [Identifier.SWEATER]: {
        label: 'Sweaters',
        param: 'sweaters',
      },
      [Identifier.SWEATSHIRT]: {
        label: 'Sweatshirts',
        param: 'sweatshirts',
      },
      [Identifier.HOODIE]: {
        label: 'Hoodies',
        param: 'hoodies',
      },
      [Identifier.TUNIC]: {
        label: 'Tunics',
        param: 'tunics',
      },
      [Identifier.TANK_TOP]: {
        label: 'Tank Tops',
        param: 'tank-tops',
      },
      [Identifier.BUSTIER]: {
        label: 'Bustiers',
        param: 'bustiers',
      },
    },
    [SubCategory.BOTTOMS]: {
      [Identifier.SKIRT]: {
        label: 'Skirts',
        param: 'skirts',
      },
      [Identifier.PANTS]: {
        label: 'Pants',
        param: 'pants',
      },
      [Identifier.JEANS]: {
        label: 'Jeans',
        param: 'jeans',
      },
      [Identifier.SHORTS]: {
        label: 'Shorts',
        param: 'shorts',
      },
    },
    [SubCategory.DRESSES]: {
      [Identifier.MINI]: {
        label: 'Mini',
        param: 'mini',
      },
      [Identifier.MIDI]: {
        label: 'Midi',
        param: 'midi',
      },
      [Identifier.MAXI]: {
        label: 'Maxi',
        param: 'maxi',
      },
    },
    [SubCategory.OUTERWEAR]: {
      [Identifier.COAT]: {
        label: 'Coats',
        param: 'coats',
      },
      [Identifier.JACKET]: {
        label: 'Jackets',
        param: 'jackets',
      },
      [Identifier.VEST]: {
        label: 'Vests',
        param: 'vests',
      },
    },
    [SubCategory.SUITS_AND_BLAZERS]: {
      [Identifier.PANT_SUITS]: {
        label: 'Pant Suits',
        param: 'pant-suits',
      },
      [Identifier.SKIRT_SUITS]: {
        label: 'Skirt Suits',
        param: 'skirt-suits',
      },
      [Identifier.BLAZERS]: {
        label: 'Blazers',
        param: 'blazers',
      },
    },
    [SubCategory.JUMPSUIT]: {},
  },
};

export type TechniqueMap = {
  [T in Technique]?: SubTechnique[];
};
export const TECHNIQUE_MAPPINGS: TechniqueMap = {
  [Technique.REWORK]: [
    SubTechnique.DECONSTRUCTION,
    SubTechnique.RECONSTRUCTION,
    SubTechnique.REPURPOSE,
  ],
  [Technique.STITCH]: [
    SubTechnique.EMBROIDERY,
    SubTechnique.SHASHIKO,
    SubTechnique.BORO,
  ],
  [Technique.PATCHWORK]: [SubTechnique.PIECEING, SubTechnique.QUILT],
  [Technique.EMBELLISH]: [
    SubTechnique.APPLIQUE,
    SubTechnique.BEADS,
    SubTechnique.PATCHES,
    SubTechnique.SEQUINS,
    SubTechnique.JEWELS,
    SubTechnique.TRIM,
  ],
  [Technique.DISTRESS]: [
    SubTechnique.CUTTING,
    SubTechnique.FRAYING,
    SubTechnique.SANDING,
    SubTechnique.WIRE_BRUSH,
  ],
  [Technique.WEAVE_OR_KNOT]: [
    SubTechnique.CROCHET,
    SubTechnique.KNIT,
    SubTechnique.MACRAME,
  ],
  [Technique.PAINT]: [
    SubTechnique.BRUSH,
    SubTechnique.PENS,
    SubTechnique.SPONGE,
    SubTechnique.SPLATTER,
    SubTechnique.AIR_BRUSH,
  ],
  [Technique.PRINT]: [
    SubTechnique.SILK_SCREEN,
    SubTechnique.TRANSFER,
    SubTechnique.BLOCK_PRINT,
  ],
  [Technique.DYE]: [
    SubTechnique.TIE_DYE,
    SubTechnique.BLEACH,
    SubTechnique.SHIBORI,
    SubTechnique.BATIK,
    SubTechnique.CYANOTYPE,
  ],
};
